import React, { createContext, useState, useContext, useEffect } from 'react';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  // 从本地存储加载收藏列表
  const [favorites, setFavorites] = useState(() => {
    try {
      const storedFavorites = localStorage.getItem('favorites');
      return storedFavorites ? JSON.parse(storedFavorites) : [];
    } catch (error) {
      console.error('Failed to load favorites from localStorage:', error);
      return [];
    }
  });

  // 保存收藏列表到本地存储
  useEffect(() => {
    try {
      localStorage.setItem('favorites', JSON.stringify(favorites));
    } catch (error) {
      console.error('Failed to save favorites to localStorage:', error);
    }
  }, [favorites]);

  // 检查剧集是否已收藏
  const isFavorited = (dramaId) => {
    return favorites.some(favorite => favorite.id === dramaId);
  };

  // 添加到收藏
  const addFavorite = (drama) => {
    if (!isFavorited(drama.id)) {
      // 确保所有需要的属性都存在
      const favoriteDrama = {
        id: drama.id,
        title: drama.name || drama.title,
        name: drama.name || drama.title, // 保持兼容性
        poster: drama.poster,
        favoriteCount: drama.likeCount || drama.favoriteCount || 0,
        likeCount: drama.likeCount || drama.favoriteCount || 0, // 保持兼容性
        category: drama.category || "未分类",
        views: drama.viewCount || drama.views || 0,
        viewCount: drama.viewCount || drama.views || 0, // 保持兼容性
      };
      setFavorites(prev => [...prev, favoriteDrama]);
      console.log('剧集已添加到收藏:', favoriteDrama.title);
      
      // 调用API更新服务器端的收藏次数
      updateFavoriteCount(drama.id, 1);
    }
  };

  // 从收藏中移除
  const removeFavorite = (dramaId) => {
    setFavorites(prev => prev.filter(favorite => favorite.id !== dramaId));
    console.log('剧集已从收藏中移除:', dramaId);
    
    // 调用API更新服务器端的收藏次数
    updateFavoriteCount(dramaId, -1);
  };

  // 更新服务器端收藏次数
  const updateFavoriteCount = async (dramaId, change) => {
    try {
      const response = await fetch(`/api/dramas/${dramaId}/favorites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ change }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('收藏次数更新成功:', data.favoriteCount);
        } else {
          console.error('更新收藏次数失败:', data.error);
        }
      } else {
        console.error('更新收藏次数失败:', response.statusText);
      }
    } catch (error) {
      console.error('更新收藏次数失败:', error);
    }
  };

  // 切换收藏状态
  const toggleFavorite = (drama) => {
    if (isFavorited(drama.id)) {
      removeFavorite(drama.id);
    } else {
      addFavorite(drama);
    }
  };

  // 获取收藏数量
  const getFavoriteCount = (dramaId) => {
    // 这里可以根据需要返回真实的收藏数量，目前简化为检查是否收藏
    return isFavorited(dramaId) ? 1 : 0;
  };

  const value = {
    favorites,
    isFavorited,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    getFavoriteCount,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
